import React from "react";
import ReactMarkdown from "react-markdown";
import { FileEmbed } from "@/modules/hub/features/ResourceLibrary/components/FileEmbed/FileEmbed";
import { VideoEmbed } from "@/modules/hub/features/ResourceLibrary/components/VideoEmbed/VideoEmbed";
import { CourseContentBlock } from "@/modules/learningCenter/features/CourseDirectory/types/viewer";
import { KnowledgeCheckPlayer } from "@/modules/learningCenter/features/CourseDirectory/components/KnowledgeCheckPlayer/KnowledgeCheckPlayer";
import {
  parseCalloutMeta,
  parseReflectionMeta,
} from "@/modules/learningCenter/features/CourseManagement/types/course";

interface CourseContentBlockRendererProps {
  block: CourseContentBlock;
}

// Reads a string off the block metadata bag, tolerating absent/non-string
// values (metadata is an open `Record<string, unknown>` on the wire).
function metaString(
  metadata: Record<string, unknown> | null,
  key: string,
): string | null {
  const value = metadata?.[key];
  return typeof value === "string" && value.trim() !== "" ? value : null;
}

/**
 * Learner-facing renderer for a single authored content block. Every authored
 * type (text / image / file / video) renders — there is no silent
 * `default: return null` drop. `text` is rich markdown (Remirror produces
 * markdown), rendered with the shared `prose` styling. `file` and `video`
 * reuse the Resource Library embeds.
 */
export function CourseContentBlockRenderer({
  block,
}: CourseContentBlockRendererProps) {
  const content = block.content ?? "";
  const title = metaString(block.metadata, "title");
  const description = metaString(block.metadata, "description");

  switch (block.type) {
    case "text":
      return (
        <div className="prose dark:prose-invert max-w-none text-gray-700 dark:text-gray-200">
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
      );

    case "image":
      if (!content) {
        return (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No image provided.
          </p>
        );
      }
      return (
        <figure className="my-6">
          <img
            alt={title ?? "Course image"}
            className="rounded-lg max-w-full h-auto border border-gray-200 dark:border-slate-700"
            src={content}
          />
          {(title || description) && (
            <figcaption className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {title ?? description}
            </figcaption>
          )}
        </figure>
      );

    case "video":
      if (!content) {
        return (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No video provided.
          </p>
        );
      }
      return (
        <div className="relative my-6 w-full overflow-hidden rounded-lg bg-black aspect-video">
          <VideoEmbed contentUrl={content} />
        </div>
      );

    case "file":
      return (
        <FileEmbed
          contentUrl={content}
          description={description}
          title={title}
        />
      );

    case "callout": {
      const { label } = parseCalloutMeta(block.metadata);
      return (
        <div className="my-6 rounded-r-lg border-l-4 border-primary bg-gray-50 dark:bg-slate-800/50 px-4 py-3">
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {label}
          </p>
          <div className="prose dark:prose-invert mt-1 max-w-none text-gray-700 dark:text-gray-200">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        </div>
      );
    }

    case "reflection": {
      const { prompts } = parseReflectionMeta(block.metadata);
      if (prompts.length === 0) {
        return (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No reflection prompts provided.
          </p>
        );
      }
      return (
        <div className="my-6 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
          <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
            Take a moment to reflect:
          </p>
          <ol className="list-decimal space-y-1 pl-5 text-gray-700 dark:text-gray-200">
            {prompts.map((prompt, index) => (
              <li key={index}>{prompt}</li>
            ))}
          </ol>
        </div>
      );
    }

    case "knowledge_check":
      return <KnowledgeCheckPlayer block={block} />;

    default:
      // Unknown types are surfaced (never silently dropped) so authoring gaps
      // are visible to the learner and to QA.
      return (
        <div className="my-4 rounded-lg border border-dashed border-gray-300 dark:border-slate-600 p-4 text-sm text-gray-500 dark:text-gray-400">
          Unsupported content block type: {block.type}
        </div>
      );
  }
}

export default CourseContentBlockRenderer;
